function round2(n) {
  return Math.round(n * 100) / 100;
}

function standardUtilization(active, poolMax) {
  if (!poolMax) return 0;
  return round2((active / poolMax) * 100);
}

function coerceSnapshots(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

function cardPeak(analysis) {
  const value =
    analysis.summary?.peakUtilization ??
    analysis.peakUtilization ??
    analysis.peak_utilization_percent;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : null;
}

function readHistogram(analysis) {
  const histogram = analysis.held_time_histogram ?? analysis.holdTimeHistogram;
  return histogram && typeof histogram === 'object' ? histogram : null;
}

function readThroughput(analysis) {
  const series = analysis.hourly_breakdown || analysis.hourlyBreakdown;
  if (!Array.isArray(series) || series.length === 0) return [];
  return series
    .map((point) => {
      const tMs = Date.parse(point.t);
      if (Number.isNaN(tMs)) return null;
      return {
        tMs,
        acquires: point.acquires || 0,
        releases: point.releases || 0
      };
    })
    .filter(Boolean);
}

function seriesPeakAt(values, card) {
  if (card == null) return { value: null, at: 'unlocated' };
  let at = 'unlocated';
  for (const point of values) {
    if (point == null || !Number.isFinite(point.max) || !Number.isFinite(point.tMs)) continue;
    if (Math.abs(point.max - card) < 0.005) {
      at = point.tMs;
      break;
    }
  }
  return { value: card, at };
}

function fetchMeta(analysis) {
  return analysis.fetch_metadata || analysis.metadata?.fetchMetadata || analysis.fetchMetadata || null;
}

function requestedRangeLabel(hours) {
  const n = Number(hours);
  if (n === 168) return '7 days';
  if (n === 24) return '24 hours';
  if (n === 1) return '1 hour';
  if (Number.isFinite(n)) return `${n} hours`;
  return 'requested range';
}

function peaksNewestFirst(peaks) {
  return (Array.isArray(peaks) ? peaks : [])
    .slice()
    .sort((a, b) => (Number(b.tMs) || 0) - (Number(a.tMs) || 0));
}

function sampledSpan(buckets) {
  let startMs = null;
  let endMs = null;
  for (const bucket of buckets) {
    if (!bucket || bucket.samples === 0) continue;
    if (!Number.isFinite(bucket.tMs)) continue;
    if (startMs == null || bucket.tMs < startMs) startMs = bucket.tMs;
    if (endMs == null || bucket.tMs > endMs) endMs = bucket.tMs;
  }
  return { startMs, endMs };
}

function envelopeDomain(envelope, analysis, buckets) {
  const meta = fetchMeta(analysis);
  const coverageStart = Date.parse(meta?.connCoverage?.start);
  const coverageEnd = Date.parse(meta?.connCoverage?.end);
  if (Number.isFinite(coverageStart) && Number.isFinite(coverageEnd) && coverageEnd > coverageStart) {
    return { startMs: coverageStart, endMs: coverageEnd };
  }

  const sampled = sampledSpan(buckets);
  if (Number.isFinite(sampled.startMs) && Number.isFinite(sampled.endMs)) {
    return sampled;
  }

  const window = envelope.window || {};
  const startMs = Number.isFinite(window.startMs) ? window.startMs : (buckets[0]?.tMs ?? null);
  const endMs = Number.isFinite(window.endMs) ? window.endMs : (buckets[buckets.length - 1]?.tMs ?? null);
  return { startMs, endMs };
}

function envelopeNotices(analysis, domain, requestedWindow) {
  const meta = fetchMeta(analysis);
  const requestedHours =
    meta?.requested?.timeRangeHours ||
    analysis.metadata?.timeRangeHours ||
    analysis.time_range_hours;
  const label = requestedRangeLabel(requestedHours);
  const requestedStart = Number.isFinite(requestedWindow?.startMs) ? requestedWindow.startMs : null;
  const coversLessThanRequest =
    Number.isFinite(domain.startMs) &&
    Number.isFinite(requestedStart) &&
    domain.startMs - requestedStart > 60 * 60 * 1000;

  if (meta?.truncated || coversLessThanRequest) {
    return [`CONN samples cover a shorter window than the requested ${label}.`];
  }
  return [];
}

function fromEnvelope(envelope, analysis) {
  const buckets = Array.isArray(envelope.buckets) ? envelope.buckets : [];
  const utilization = buckets.map((bucket) => ({
    tMs: bucket.tMs,
    actual: bucket.util ? bucket.util.max : null,
    min: bucket.util ? bucket.util.min : null,
    mean: bucket.util ? bucket.util.mean : null,
    max: bucket.util ? bucket.util.max : null,
    span: bucket.util ? bucket.util.max - bucket.util.min : null
  }));
  const connections = buckets.map((bucket) => ({
    tMs: bucket.tMs,
    active: bucket.active ? bucket.active.mean : null,
    idle: bucket.idle ? bucket.idle.mean : null
  }));
  const peakPoints = utilization
    .filter((point) => point.max != null)
    .map((point) => {
      const bucket = buckets.find((b) => b.tMs === point.tMs);
      return { tMs: bucket?.peakAtMs ?? point.tMs, max: point.max };
    });
  const throughput = readThroughput(analysis);
  const throughputFromBuckets =
    throughput.length > 0
      ? throughput
      : buckets.map((bucket) => ({
          tMs: bucket.tMs,
          acquires: bucket.acquires || 0,
          releases: bucket.releases || 0
        }));
  const domain = envelopeDomain(envelope, analysis, buckets);

  return {
    kind: 'envelope',
    assumedMax: false,
    poolMax: envelope.poolMax,
    domain,
    utilization,
    connections,
    throughput: throughputFromBuckets,
    peak: seriesPeakAt(peakPoints, cardPeak(analysis)),
    peaks: peaksNewestFirst(envelope.peaks),
    histogram: readHistogram(analysis),
    notices: envelopeNotices(analysis, domain, envelope.window)
  };
}

function fromLegacyArray(snapshots, analysis) {
  let poolMax = null;
  let assumedMax = false;
  for (const snapshot of snapshots) {
    const max = snapshot?.standard?.maxConnections;
    if (typeof max === 'number' && max > 0) {
      poolMax = max;
      break;
    }
  }
  if (poolMax == null) {
    assumedMax = true;
    poolMax = 30;
  }

  const points = [];
  for (const snapshot of snapshots) {
    if (!snapshot?.standard) continue;
    const tMs = Date.parse(snapshot.timestamp);
    if (Number.isNaN(tMs)) continue;
    const total = Number(snapshot.standard.total) || 0;
    const idle = Number(snapshot.standard.idle) || 0;
    const active = total - idle;
    const utilization = standardUtilization(active, poolMax);
    points.push({
      tMs,
      min: utilization,
      mean: utilization,
      max: utilization,
      span: 0,
      active,
      idle,
      waiting: Number(snapshot.standard.waiting) || 0
    });
  }
  points.sort((a, b) => a.tMs - b.tMs);

  const notices = assumedMax
    ? ['This history row is a sparse sample. Utilization uses an assumed pool max of 30 and is not exact provenance.']
    : ['This history row is a sparse sample. A short spike may be missing.'];

  return {
    kind: 'legacy',
    assumedMax,
    poolMax,
    domain: {
      startMs: points[0]?.tMs ?? null,
      endMs: points[points.length - 1]?.tMs ?? null
    },
    utilization: points.map((point) => ({
      tMs: point.tMs,
      actual: point.max,
      min: point.min,
      mean: point.mean,
      max: point.max,
      span: point.span
    })),
    connections: points.map((point) => ({
      tMs: point.tMs,
      active: point.active,
      idle: point.idle
    })),
    throughput: readThroughput(analysis),
    peak: seriesPeakAt(points, cardPeak(analysis)),
    peaks: peaksNewestFirst(
      points
        .slice()
        .sort((a, b) => b.max - a.max)
        .slice(0, 20)
        .map((point) => ({
          tMs: point.tMs,
          utilization: point.max,
          active: point.active,
          idle: point.idle,
          waiting: point.waiting
        }))
    ),
    histogram: readHistogram(analysis),
    notices
  };
}

export function readPoolStats(analysis) {
  if (!analysis) return null;
  const raw = coerceSnapshots(analysis.pool_snapshots ?? analysis.poolSnapshots);
  if (!raw) {
    const histogram = readHistogram(analysis);
    const throughput = readThroughput(analysis);
    if (!histogram && throughput.length === 0) return null;
    return {
      kind: 'legacy',
      assumedMax: false,
      poolMax: null,
      domain: { startMs: null, endMs: null },
      utilization: [],
      connections: [],
      throughput,
      peak: { value: cardPeak(analysis), at: 'unlocated' },
      peaks: [],
      histogram,
      notices: []
    };
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.kind === 'envelope') {
    return fromEnvelope(raw, analysis);
  }

  if (Array.isArray(raw)) {
    if (raw.length === 0) {
      const histogram = readHistogram(analysis);
      const throughput = readThroughput(analysis);
      if (!histogram && throughput.length === 0) return null;
      return {
        kind: 'legacy',
        assumedMax: false,
        poolMax: null,
        domain: { startMs: null, endMs: null },
        utilization: [],
        connections: [],
        throughput,
        peak: { value: cardPeak(analysis), at: 'unlocated' },
        peaks: [],
        histogram,
        notices: []
      };
    }
    return fromLegacyArray(raw, analysis);
  }

  return null;
}
